import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Grave } from '../../../core/models/grave.model';
import { GenericResponse } from '../../../core/models/pagination.model';
import { GraveService } from '../../../core/services/grave.service';
import { CommonModule, DatePipe } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { GoogleMapsApiService } from '../../../core/services/google-maps-api.service';
import { ArMapComponent } from './ar-map/ar-map.component';
import { HasRoleDirective } from '../../../shared/directives/hasRole.directive';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { EmailService } from '../../../services/email.service';
import { SendGraveEmailDto } from '../../../models/send-grave-email.model';
import { TextAreaComponent } from '../../../shared/components/text-area/text-area.component';

@Component({
  selector: 'app-grave-search-details',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    GoogleMapsModule,
    TranslateModule,
    HasRoleDirective,
    ReactiveFormsModule,
    TextAreaComponent
  ],
  templateUrl: './grave-search-details.component.html',
  styleUrl: './grave-search-details.component.scss',
  providers: [GraveService]
})
export class GraveSearchDetailsComponent implements OnInit {
  // For zoomable image modal
  modalImageSrc: string | null = null;
  modalImageAlt: string = '';
  // isImageModalOpen = false;

  // Google Maps configuration
  center: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  zoom = 20;
  markerPosition: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  markerOptions: any = { draggable: false };
  mapOptions: any = {
    mapTypeId: 'satellite',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    maxZoom: 20,
    minZoom: 8,
    mapId: 'GRAVE_MAP' // Required for advanced markers
  };


  grave: Grave | null = null;
  loading = true;
  error: string | null = null;
  apiLoaded = false; // Flag to track Google Maps API loading status
  isBrowser: boolean;
  isImageModalOpen = false; // Flag to control the image modal visibility
  showARMap = false; // Flag to toggle AR map view

  // Suggestion modal properties
  isSuggestionModalOpen = false;
  suggestionForm: FormGroup;
  isSubmitting = false;
  submitted = false;
  showSuccessToast = false;
  showErrorToast = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private graveService: GraveService,
    private mapsApiService: GoogleMapsApiService,
    public translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private formBuilder: FormBuilder,
    private emailService: EmailService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Initialize suggestion form with validators
    this.suggestionForm = this.formBuilder.group({
      additionalMessage: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]]
    });
  }

  ngOnInit(): void {
    this.loading = true;

    // Get the grave ID from the route
    const id = String(this.route.snapshot.paramMap.get("id"));
    // if (!id) {
    //   this.error = "Invalid grave ID";
    //   this.loading = false;
    //   return;
    // }

    // Always get grave details
    this.getGraveDetails(id);

    // Only load Google Maps in browser environment
    if (this.isBrowser) {
      this.loadGoogleMaps();
    } else {
      // Mark as not loaded in server environment
      this.apiLoaded = false;
    }
  }

  loadGoogleMaps(): void {
    this.mapsApiService.loadApi()
      .then(() => {
        this.apiLoaded = true;
        // Now that the API is loaded, we can safely use Google Maps types
        if (this.isBrowser && google && google.maps) {
          this.markerOptions.animation = google.maps.Animation.DROP;
        }

        // Initialize map if grave details are already loaded
        if (this.grave && this.grave.latitude && this.grave.longitude) {
          this.initializeMap();
        }
      })
      .catch(err => {
        console.error('Error loading Google Maps API:', err);
        this.error = "Failed to load Google Maps";
      });
  }

  getGraveDetails(id: string): void {
    this.graveService.getGraveById(id).subscribe({
      next: (response: GenericResponse<Grave>) => {
        this.grave = response.data;
        this.loading = false;

        if (this.isBrowser && this.apiLoaded && this.grave && this.grave.latitude && this.grave.longitude) {
          this.initializeMap();
        }
      },
      error: (err: any) => {
        console.error("Error fetching grave details:", err);
        this.error = "Failed to load grave details";
        this.loading = false;
      }
    });
  }

  // Method to open zoomable image modal for grave/certificates
  openImageModal(type: 'grave' | 'death' | 'body') {
    if (!this.grave) return;
    if (type === 'grave' && this.grave.imageURLBase64) {
      this.modalImageSrc = 'data:image/jpg;base64,' + this.grave.imageURLBase64;
      this.modalImageAlt = this.translate.currentLang === 'ar' ? this.grave.fullNameAr : this.grave.fullNameEn;
      this.isImageModalOpen = true;
    } else if (type === 'death' && this.grave.deathCertificateImageURLBase64) {
      this.modalImageSrc = 'data:image/jpeg;base64,' + this.grave.deathCertificateImageURLBase64;
      this.modalImageAlt = this.translate.instant('GRAVE_DETAILS.DEATH_CERTIFICATE');
      this.isImageModalOpen = true;
    } else if (type === 'body' && this.grave.bodyReceiptCertificateImageURLBase64) {
      this.modalImageSrc = 'data:image/jpeg;base64,' + this.grave.bodyReceiptCertificateImageURLBase64;
      this.modalImageAlt = this.translate.instant('GRAVE_DETAILS.BODY_CERTIFICATE');
      this.isImageModalOpen = true;
    }
  }

  // Call this to close the modal and reset image
  closeImageModal() {
    this.isImageModalOpen = false;
    this.modalImageSrc = null;
    this.modalImageAlt = '';
  }

  initializeMap(): void {
    if (!this.isBrowser || !this.grave || !this.grave.latitude || !this.grave.longitude) {
      return;
    }

    // Set up map center and marker position
    this.center = {
      lat: this.grave.latitude,
      lng: this.grave.longitude
    };

    this.markerPosition = {
      lat: this.grave.latitude,
      lng: this.grave.longitude
    };
  }

  // Method to handle opening Google Maps navigation
  navigateToGoogleMaps(): void {
    if (!this.grave || !this.grave.latitude || !this.grave.longitude) return;
    const lat = this.grave.latitude;
    const lng = this.grave.longitude;
    // Google Maps navigation URL
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
    window.open(url, '_blank');
  }

  // Method to handle opening the AR experience
  openARExperience(): void {
    if (!this.grave) return;

    // Toggle AR map view - this will activate the AR component
    // which uses AR.js to display a 3D marker at the grave coordinates
    // The AR component will request camera and location permissions
    // this.showARMap = true; // Commented out

    // For debugging - log the coordinates
    console.log('Navigating to AR experience for coordinates:',
      this.grave.latitude, this.grave.longitude);

    // Uncomment this if you want to navigate to a separate AR view instead - Now uncommented
    // this.grave.latitude = 23.583418;
    // this.grave.longitude = 58.435188;
    this.router.navigate(['/ar-view'], {
      state: { grave: this.grave }
    });
  }

  goBack(): void {
    try {
      this.location.back();
    } catch (error) {
      // Fallback to home page if there's an error
      this.router.navigate(['/']);
    }
  }

  // Getter for form controls
  get f() { return this.suggestionForm.controls; }

  /**
   * Opens the suggestion modal
   */
  openSuggestionModal(): void {
    this.isSuggestionModalOpen = true;
    this.submitted = false;
    this.suggestionForm.reset();
  }

  /**
   * Submits the suggestion form
   */
  submitSuggestion(): void {
    console.log(this.grave);

    this.submitted = true;

    // Stop if form is invalid
    if (this.suggestionForm.invalid) {
      return;
    }

    this.isSubmitting = true;

    const emailData: SendGraveEmailDto = {
      graveId: this.grave?.graveId || '',
      additionalMessage: this.suggestionForm.value.additionalMessage
    };

    this.emailService.sendGraveEmail(emailData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isSuggestionModalOpen = false;
        this.showSuccessToast = true;

        // Hide success toast after 5 seconds
        setTimeout(() => {
          this.showSuccessToast = false;
        }, 5000);
      },
      error: (err) => {
        console.error('Error sending suggestion email:', err);
        this.isSubmitting = false;
        this.showErrorToast = true;

        // Hide error toast after 5 seconds
        setTimeout(() => {
          this.showErrorToast = false;
        }, 5000);
      }
    });
  }
}
